import type { FlowExecutionType } from "@/types/entities/flows";
import { formatDateTime } from "@/utils/dates";
import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { getExecutionBadge } from ".";
import {
  useDownloadGeneratedDocument,
  useGetGeneratedDocument,
} from "@/queries/document-templates/document-templates.api";

interface Props {
  execution: FlowExecutionType["node_executions"][number];
}

interface OutputNameConfig {
  base: string;
  include_request_id: boolean;
  include_date: boolean;
}

const DocumentGeneratorContent = ({ execution }: Props) => {
  const templateName = execution.inputs?.find(
    (input) => input.name === "template_name",
  )?.value as string;

  const templateVersion = execution.inputs?.find(
    (input) => input.name === "template_version",
  )?.value as string;

  const outputNameRaw = execution.inputs?.find(
    (input) => input.name === "output_name",
  )?.value as string;

  let outputNameConfig: OutputNameConfig | null = null;
  try {
    outputNameConfig = outputNameRaw ? JSON.parse(outputNameRaw) : null;
  } catch {
    outputNameConfig = null;
  }

  const fileName =
    outputNameConfig?.base ||
    (execution.inputs?.find((input) => input.name === "file_name")
      ?.value as string);

  const resultId = execution.outputs?.find((output) => output.name === "result")
    ?.value as string;

  const { data: generatedDocument, isLoading } =
    useGetGeneratedDocument(resultId);
  const { mutate: downloadDocument, isPending: isDownloading } =
    useDownloadGeneratedDocument();

  const media = generatedDocument?.media?.[0];
  const finalFileName = media?.file_name;

  if (typeof resultId === "object") {
    const message =
      (resultId as { message?: string }).message ||
      "Ocurrió un error desconocido.";
    return (
      <div className="w-full flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p>Estado del nodo: {getExecutionBadge("FAILED")}</p>
        </div>

        <div className="border border-neutral rounded-lg p-4 flex flex-col gap-3">
          {resultId && <p className="font-normal text-body-quiet">{message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p>Estado del nodo: {getExecutionBadge(execution.status)}</p>
      </div>

      <div className="border border-neutral rounded-lg p-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <Icon
            type="far"
            name="file-pdf"
            className="text-danger text-xl flex-shrink-0 mt-1"
          />
          <div className="flex flex-col gap-2 min-w-0 flex-1">
            <div className="flex flex-col">
              <span className="font-semibold text-body-loud break-all">
                {isLoading ? "Cargando..." : finalFileName || `${fileName}.pdf`}
              </span>
              <span className="text-sm text-body-quiet">
                Creado: {formatDateTime(execution.started_at)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-body-quiet">Plantilla:</span>
          <span className="text-body-loud">
            {templateName || "No disponible"}
          </span>
          {templateVersion && (
            <Badge variant="neutral" size="sm">
              v{templateVersion}
            </Badge>
          )}
        </div>

        {resultId && (
          <Button
            variant="quiet"
            size="sm"
            onClick={() => downloadDocument(resultId)}
            disabled={isDownloading}
            className="w-fit"
          >
            <Icon
              type="far"
              name={isDownloading ? "spinner-third" : "download"}
              className={isDownloading ? "animate-spin" : ""}
            />
            {isDownloading ? "Descargando..." : "Visualizar"}
          </Button>
        )}

        {outputNameConfig && (
          <div className="flex flex-col gap-1 text-sm border-t border-neutral pt-3 mt-1">
            <span className="text-body-quiet font-semibold mb-1">
              Configuración del nombre:
            </span>
            <div className="flex items-center gap-2">
              <Icon
                type="far"
                name={outputNameConfig.include_date ? "check" : "xmark"}
                className={
                  outputNameConfig.include_date
                    ? "text-success"
                    : "text-body-quiet"
                }
              />
              <span
                className={
                  outputNameConfig.include_date
                    ? "text-body-loud"
                    : "text-body-quiet"
                }
              >
                Fecha
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon
                type="far"
                name={outputNameConfig.include_request_id ? "check" : "xmark"}
                className={
                  outputNameConfig.include_request_id
                    ? "text-success"
                    : "text-body-quiet"
                }
              />
              <span
                className={
                  outputNameConfig.include_request_id
                    ? "text-body-loud"
                    : "text-body-quiet"
                }
              >
                ID de solicitud
              </span>
            </div>
          </div>
        )}

        {resultId && (
          <div className="flex items-center gap-2 text-sm border-t border-neutral pt-3 mt-1">
            <span className="text-body-quiet">ID del documento:</span>
            <span className="text-body font-mono text-xs">{resultId}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentGeneratorContent;
